import {
  DocumentSummarySection,
  DocumentKeyPointsSection,
  DocumentDetailsSection,
  SidebarEyebrow,
  documentKeyPoints,
} from '@/components/shared-ui/document-insights';
import { cn } from '@listening/utils/react-helpers';
import type { Paragraph } from '@listening/api-contract/legacy-client-types';
import { useAnalytics, useFlag } from '@listening/frontend/analytics/client';
import { Button } from '@listening/frontend/components/radix/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@listening/frontend/components/radix/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@listening/frontend/components/radix/tooltip';
import {
  getDocContentWidthClassName,
  getDocFontClassName,
  getDocFontWeightClassName,
  getDocLineHeightClassName,
  getDocParagraphSpacingClassName,
  getDocTextSizeClassName,
  useAudioPrefsStore,
  useReadAlongMainContentView,
} from '@listening/frontend/stores/audio-preferences-store';
import React, {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { ErrorComponent } from '@/components/shared-ui/error-component';
import { LoadingComponent } from '@/components/shared-ui/loading-component';
import {
  JumpToCurrentButton,
  useJumpToCurrentButtonVisibility,
} from '@/components/start/read-along/read-along-jump-to-current-button';
import {
  type ReadAlongContextStoreProps,
  ReadAlongStoreProvider,
  useReadAlongContext,
} from '@/components/start/read-along/read-along-store';
import {
  LoadingPdfPageRender,
  type WordRenderProps,
} from '@/components/start/read-along/read-along-renders';
import { useLegacyWordRegistryRef } from '@/lib/doc-reader/highlight/highlight-registry';
import { ReadAlongSideBar } from '@/components/start/read-along/read-along-side-bar';
import {
  fetchChaptersFn,
  useLoadAudioFromSlug,
} from '@/components/start/read-along/read-along-utils';
import { isGeneratedChapterTitle } from '@/lib/audio-display-metadata';
import { useDocumentSummary } from '@/lib/audio-chunk-player';
import type { DocReaderViewHandle } from '@/lib/doc-reader';
import type { ReaderRenderOverrides } from '@/lib/doc-reader/reader/reader-common';
import { rootLogger } from '@/lib/root-logger';
import { DocsTopBar } from './docs-top-bar';
import { getDocsActiveWordClassName } from './docs-text-reader-style';
import { estimateReadingTimeMinutes } from './reading-time';
import type { GoogleDocPublicPdfData } from '@listening/frontend/public-doc-catalog/types';
import { ClientOnly, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { resolvePublicDocCategory } from '@listening/api-contract';
import type { EdgeBucketedFlagKey } from '@listening/frontend/analytics/define-route-analytics';
import { docPackOutlineNavigationItems } from '@listening/frontend/doc-pack/navigation';
import { docPackCatalogArgs } from '@listening/frontend/doc-pack/source';
import { useEq } from '@listening/frontend/query/runtime-context';
import {
  ArrowLeft,
  Compress,
  Download,
  LightBulb,
  NavArrowDown,
  NavArrowLeft,
  NavArrowUp,
  Page,
  Search,
  Xmark,
} from 'iconoir-react';
import {
  useDocSearchStore,
  useIsSearchMatch,
} from '@/lib/doc-reader/search/doc-search-store';
import { useDocSearch } from '@/lib/doc-reader/search/use-doc-search';
import {
  useDocPackUsable,
  useWarmDocPack,
} from '@/lib/doc-reader/pack/doc-pack-source';
import { usePackAwareFetchChapters } from '@/lib/doc-reader/pack/doc-pack-chapters';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';

import { LazyDocumentView, LazyDocPackReaderView } from '@/lib/doc-reader/lazy';
import { DocReaderProvider } from '@/lib/doc-reader';
import { ReaderViewTanStack } from '@/lib/doc-reader/reader/reader-tanstack';

const PDF_SCALE_FULL = 1.6;

const mql1200 =
  typeof window !== 'undefined'
    ? window.matchMedia('(min-width: 1200px)')
    : null;
const mqlLg =
  typeof window !== 'undefined'
    ? window.matchMedia('(min-width: 1024px)')
    : null;

function docsBasePdfScaleSubscribe(cb: () => void) {
  mql1200?.addEventListener('change', cb);
  mqlLg?.addEventListener('change', cb);
  return () => {
    mql1200?.removeEventListener('change', cb);
    mqlLg?.removeEventListener('change', cb);
  };
}

function docsBasePdfScaleSnapshot() {
  if (mql1200?.matches) return PDF_SCALE_FULL * 0.8;
  if (mqlLg?.matches) return PDF_SCALE_FULL * 0.7;
  return PDF_SCALE_FULL * 0.7;
}

function useDocsBasePdfScale() {
  return useSyncExternalStore(
    docsBasePdfScaleSubscribe,
    docsBasePdfScaleSnapshot,
    () => PDF_SCALE_FULL,
  );
}

function isLgViewportSubscribe(cb: () => void) {
  mqlLg?.addEventListener('change', cb);
  return () => mqlLg?.removeEventListener('change', cb);
}

function useIsLgViewport() {
  return useSyncExternalStore(
    isLgViewportSubscribe,
    () => mqlLg?.matches ?? true,
    () => true,
  );
}

type DbDocData = {
  summary: string | null;
  keyPoints: string | null;
  description: string | null;
  category: string | null;
  coverImageUrl: string | null;
  publishingAuthor: string | null;
} | null;

type RelatedPublicDoc = {
  publicId: string;
  slug: string;
  title: string | null;
  description: string | null;
  coverImageUrl?: string | null;
};

type DocsLeftSidebarItem =
  | {
      type: 'chapter';
      id: string;
      title: string;
    }
  | {
      type: 'doc-pack';
      id: string;
      title: string;
      scrollableIdx: number;
      depth: number;
    };

function useNavbarHidden() {
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    let lastScrollTop = 0;
    let ticking = false;

    const updateScrollDir = (scrollTop: number) => {
      const diff = scrollTop - lastScrollTop;
      if (scrollTop < 50) {
        setIsHidden(false);
      } else if (diff > 8) {
        setIsHidden(true);
      } else if (diff < -8) {
        setIsHidden(false);
      }
      lastScrollTop = scrollTop;
      ticking = false;
    };

    const onScroll = (e: Event) => {
      if (ticking) return;
      const target = e.target as HTMLElement | Document;
      let scrollTop = 0;
      if (target === document || target === document.documentElement) {
        scrollTop = window.scrollY || document.documentElement.scrollTop;
      } else if (target instanceof HTMLElement) {
        scrollTop = target.scrollTop;
      }
      window.requestAnimationFrame(() => updateScrollDir(scrollTop));
      ticking = true;
    };

    document.addEventListener('scroll', onScroll, {
      passive: true,
      capture: true,
    });
    return () =>
      document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  return isHidden;
}

export function DocsReadAlongPage({
  slug,
  title,
  dbDoc,
  experimentNames,
  onMobileReaderOpenChange,
  overviewPopupVariant,
  relatedDocs,
  showMobileDocumentLanding,
  storeProps,
}: {
  slug: string;
  title: string;
  dbDoc?: DbDocData;
  experimentNames?: ReadonlyArray<EdgeBucketedFlagKey>;
  onMobileReaderOpenChange: (open: boolean) => void;
  overviewPopupVariant: 'control' | 'treatment';
  relatedDocs?: ReadonlyArray<RelatedPublicDoc>;
  showMobileDocumentLanding: boolean;
  storeProps?: Partial<ReadAlongContextStoreProps>;
}) {
  useLoadAudioFromSlug(slug);
  const { value: titleVariant } = useFlag('marketingPublicDocGiantTitleV1');
  const { value: readingTimeVariant } = useFlag('marketingPublicDocReadTimeV1');
  const showGiantTitle = titleVariant === 'treatment';
  const showReadingTime = readingTimeVariant === 'treatment';
  const showOverviewPopup = overviewPopupVariant === 'treatment';
  const docFullscreen = useAudioPrefsStore((s) => s.docFullscreen);
  const setDocFullscreen = useAudioPrefsStore((s) => s.setDocFullscreen);
  const autoScroll = useAudioPrefsStore((s) => s.autoScroll);
  const setAutoScroll = useAudioPrefsStore((s) => s.setAutoScroll);
  const isNavbarHidden = useNavbarHidden();

  const mobileReaderControlsEnabled = !showMobileDocumentLanding;
  const effectiveDocFullscreen = docFullscreen && mobileReaderControlsEnabled;
  const showAutoScrollToggle =
    mobileReaderControlsEnabled &&
    autoScroll &&
    (isNavbarHidden || effectiveDocFullscreen);

  return (
    <ReadAlongStoreProvider {...storeProps}>
      {!showGiantTitle && (
        <h1
          className={cn(
            'sr-only',
            showMobileDocumentLanding && 'max-lg:hidden',
          )}
        >
          {title}
        </h1>
      )}
      {/* No-JS path: when JS is disabled (search-engine HTML-only bot, or a
          user who blocked JS) we want the chapter text — which the SSR
          already emits — to be the visible default, not the PDF skeleton.
          With JS the same SSR HTML hydrates into the existing PDF-default
          UX. <style> inside <noscript> only applies when the browser will
          not execute scripts, so JS users never see these overrides. */}
      <noscript>
        <style>{`[data-text-reader-ssr]{position:static!important;clip:auto!important;clip-path:none!important;width:auto!important;height:100%!important;padding:0!important;margin:0!important;overflow:visible!important;white-space:normal!important}[data-pdf-skeleton],[data-mobile-document-landing]{display:none!important}[data-mobile-document-reader]{display:flex!important}`}</style>
      </noscript>
      {/* Fullscreen is a mobile-only chrome-hiding mode (the entry toggle in
          DocsTopBar is `sm:hidden`). The persisted `docFullscreen` flag can
          survive a resize/rotation onto a wide viewport, so we apply the
          fullscreen layout effect ONLY at <sm. At >=sm the chrome stays
          mounted and visible regardless of the flag, which prevents the
          "trapped on desktop with no exit" path. */}
      <article
        data-experiment-variant={titleVariant}
        data-overview-popup-experiment-variant={overviewPopupVariant}
        data-reading-time-experiment-variant={readingTimeVariant}
        className={cn(
          'relative grid h-full w-full min-w-0 overflow-clip',
          'grid-rows-[min-content_1fr] sm:grid-cols-[1fr_min-content]',
          effectiveDocFullscreen && 'max-sm:grid-rows-[1fr]',
        )}
      >
        <DocsTopBar
          className={cn(effectiveDocFullscreen && 'max-sm:hidden')}
          experimentNames={experimentNames}
          keepMobileHeaderVisible={showMobileDocumentLanding}
          mobileReaderControlsEnabled={mobileReaderControlsEnabled}
        />
        <ReadAlongSideBar
          className={cn(
            'row-span-2',
            effectiveDocFullscreen && 'max-sm:hidden',
          )}
        />
        <DocsMainContent
          dbDoc={dbDoc}
          onMobileReaderOpenChange={onMobileReaderOpenChange}
          relatedDocs={relatedDocs}
          showMobileDocumentLanding={showMobileDocumentLanding}
          showOverviewPopup={showOverviewPopup}
          title={title}
          showGiantTitle={showGiantTitle}
          showReadingTime={showReadingTime}
        />
        {effectiveDocFullscreen && (
          <button
            type='button'
            className='fixed top-4 right-4 z-50 flex size-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-opacity hover:bg-black/80 sm:hidden'
            onClick={() => setDocFullscreen(false)}
            aria-label='Exit fullscreen'
          >
            <Compress className='size-5' />
          </button>
        )}
        {showAutoScrollToggle && (
          <button
            type='button'
            className='fixed right-4 bottom-20 z-50 rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm transition-opacity hover:bg-black/90 sm:hidden'
            onClick={() => setAutoScroll(false)}
          >
            Disable Auto Scroll
          </button>
        )}
      </article>
    </ReadAlongStoreProvider>
  );
}

function DocsMainContent({
  dbDoc,
  onMobileReaderOpenChange,
  relatedDocs,
  showGiantTitle,
  showMobileDocumentLanding,
  showOverviewPopup,
  showReadingTime,
  title,
}: {
  dbDoc?: DbDocData;
  onMobileReaderOpenChange: (open: boolean) => void;
  relatedDocs?: ReadonlyArray<RelatedPublicDoc>;
  showGiantTitle: boolean;
  showMobileDocumentLanding: boolean;
  showOverviewPopup: boolean;
  showReadingTime: boolean;
  title: string;
}) {
  const { documentSummary, audioDetailQuery } = useDocumentSummary();

  // Saved progress decides which page the reader lands on; warm that page's
  // pack blob (falls back to page 1 when there is no progress).
  const progress = audioDetailQuery.data?.progress;
  const progressChapterId = progress?.chapter_id;
  const progressDocPackPage = progress?.doc_pack_page;
  const initialPackPage = useMemo(() => {
    if (progressDocPackPage !== null && progressDocPackPage !== undefined) {
      return progressDocPackPage;
    }
    if (!documentSummary || !progressChapterId) return 1;
    const chapter = documentSummary.chapters.find(
      (c) => c.id === progressChapterId,
    );
    return chapter?.bbox_pages[0] ?? 1;
  }, [documentSummary, progressChapterId, progressDocPackPage]);
  useWarmDocPack({
    audioId: documentSummary?.id ?? '',
    docPack: documentSummary?.doc_pack ?? undefined,
    initialPage: initialPackPage,
  });

  const fetchChapters = usePackAwareFetchChapters(fetchChaptersFn);
  const content = useReadAlongMainContentView(audioDetailQuery.data ?? null);
  const setReadAlongViewer = useAudioPrefsStore(
    (state) => state.setReadAlongViewer,
  );
  const autoScroll = useAudioPrefsStore((s) => s.autoScroll);
  const docFontClass = getDocFontClassName(
    useAudioPrefsStore((s) => s.docFont),
  );
  const docFontWeightClass = getDocFontWeightClassName(
    useAudioPrefsStore((s) => s.docFontWeight),
  );
  const docTextSizeClass = getDocTextSizeClassName(
    useAudioPrefsStore((s) => s.docTextSize),
  );
  const docContentWidthClass = getDocContentWidthClassName(
    useAudioPrefsStore((s) => s.docContentWidth),
  );

  const basePdfScale = useDocsBasePdfScale();
  const pdfScale = basePdfScale * useAudioPrefsStore((state) => state.pdfScale);
  const isLgViewport = useIsLgViewport();

  const readerDocHandleRef = useRef<DocReaderViewHandle>(null);
  const { showJumpBtn, setIsCurrentTextVisible } =
    useJumpToCurrentButtonVisibility();

  // The text reader must be in the SSR HTML for SEO regardless of viewMode.
  // After hydration, in PDF mode we unmount it: keeping it mounted while
  // sr-only would announce its content to screen readers (duplicating the
  // PDF) and keep the virtualizer + IntersectionObserver running invisibly.
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const hasSearchQuery = useDocSearchStore((s) => s.query.trim().length > 0);
  const renderOverrides = useMemo(
    (): ReaderRenderOverrides => ({
      ...docsRenderOverrides,
      forceWordRender: hasSearchQuery,
    }),
    [hasSearchQuery],
  );

  const metadata = useReadAlongContext((s) => s.metadata);
  // Unconditional hook call: must stay above the early returns below.
  const docPackUsable = useDocPackUsable(documentSummary?.doc_pack);
  const eq = useEq();
  const docPackInfo =
    documentSummary && docPackUsable ? documentSummary.doc_pack : undefined;
  const docPackCatalogQuery = useQuery({
    ...eq.queryOptions(
      docPackCatalogArgs(documentSummary?.id ?? '', docPackInfo?.url ?? ''),
    ),
    enabled: !!documentSummary && docPackInfo !== undefined,
  });

  const audioDetailTitle = audioDetailQuery.data?.title?.trim();

  const shouldAppendTitle =
    audioDetailTitle && !isGeneratedChapterTitle(audioDetailTitle) && !metadata;

  useEffect(() => {
    if (!shouldAppendTitle) return;
    const originalTitle = document.title;
    document.title = `${originalTitle} - ${audioDetailTitle}`;
    return () => {
      document.title = originalTitle;
    };
  }, [shouldAppendTitle, audioDetailTitle]);

  if (audioDetailQuery.isError)
    return (
      <div className='flex size-full items-center justify-center'>
        <ErrorComponent
          header={m.read_along_error_loading_document()}
          promptRefresh
        />
      </div>
    );

  const loadingFallback = (
    <div className='flex size-full items-center justify-center'>
      <LoadingComponent />
    </div>
  );

  // PDF skeleton for the SSR / pre-hydration / lazy-chunk-loading window so
  // PDF-mode users see a properly-sized page placeholder instead of a
  // spinner. Same component react-pdf uses internally once mounted, so the
  // visual transition is continuous: skeleton → react-pdf's own skeleton →
  // rendered page. Standard letter-size 612×792 scaled by the user's prefs.
  //
  // Layout mirrors the real PDF view's scroll container (top-aligned, padded,
  // scrollable). The SSR-side `basePdfScale` falls back to `1.6` (no
  // matchMedia on the server) so the skeleton is ~1267px tall — vertical
  // centering would overflow ~216px UP into the top-bar grid row before
  // hydration. Top-alignment + `overflow-y-auto` keeps the skeleton
  // confined to the 1fr cell. `data-pdf-skeleton` is the hook the
  // <noscript> sibling uses to hide this for no-JS visitors.
  const renderPdfLoadingFallback = (scrollHeader: React.ReactNode) => (
    <div className='size-full overflow-y-auto'>
      {scrollHeader}
      <div data-pdf-skeleton>
        <div className='flex w-full justify-center px-1 pt-2 sm:px-2 md:px-4'>
          <LoadingPdfPageRender
            estimatedSize={{
              height: 792 * pdfScale,
              width: 612 * pdfScale,
            }}
          />
        </div>
      </div>
    </div>
  );

  if (audioDetailQuery.isPending || !documentSummary) return loadingFallback;

  const useDocPackTextReader = content.viewMode !== 'pdf' && docPackUsable;
  const docPackPageIndexByNumber = new Map(
    (docPackCatalogQuery.data?.catalog.pages ?? []).map((page, index) => [
      page.n,
      index,
    ]),
  );
  const docPackSidebarItems: DocsLeftSidebarItem[] = docPackCatalogQuery.data
    ? docPackOutlineNavigationItems(docPackCatalogQuery.data.catalog).map(
        (item) => ({
          type: 'doc-pack',
          id: item.id,
          title: item.title,
          scrollableIdx: docPackPageIndexByNumber.get(item.page) ?? 0,
          depth: item.depth,
        }),
      )
    : [];
  const chapterSidebarItems: DocsLeftSidebarItem[] =
    documentSummary.chapters.map((chapter) => ({
      type: 'chapter',
      id: chapter.id,
      title: chapter.title,
    }));
  const leftSidebarItems =
    docPackSidebarItems.length > 0 ? docPackSidebarItems : chapterSidebarItems;
  const readingTimeMinutes = showReadingTime
    ? estimateReadingTimeMinutes(documentSummary.chapters)
    : null;
  const scrollHeader = (showGiantTitle || readingTimeMinutes) && (
    <DocsReaderHeader
      readingTimeMinutes={readingTimeMinutes}
      showGiantTitle={showGiantTitle}
      title={title}
    />
  );
  const pdfLoadingFallback = renderPdfLoadingFallback(scrollHeader);
  const documentUrl = content.documentUrl;
  const mobileDocumentUrl = showMobileDocumentLanding ? documentUrl : null;

  return (
    <div className='relative flex h-full min-h-0'>
      <DocsLeftSidebar
        items={leftSidebarItems}
        onItemClick={(item) => {
          if (item.type === 'chapter') {
            readerDocHandleRef.current?.scrollToChapter(item.id);
            return;
          }
          readerDocHandleRef.current?.scrollToIndex(item.scrollableIdx);
        }}
      />
      {mobileDocumentUrl && (
        <DocsMobileDocumentLanding
          authors={metadata?.authors || dbDoc?.publishingAuthor}
          category={
            dbDoc?.category ??
            (metadata?.category
              ? resolvePublicDocCategory(metadata.category)
              : null)
          }
          coverImageUrl={
            audioDetailQuery.data.cover_img_url ?? dbDoc?.coverImageUrl
          }
          documentUrl={mobileDocumentUrl}
          onViewPdf={() => {
            setReadAlongViewer('pdf');
            onMobileReaderOpenChange(true);
          }}
          pageCount={audioDetailQuery.data.page_count}
          readingTimeMinutes={readingTimeMinutes}
          source={metadata?.source}
          title={title}
        />
      )}
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col',
          showMobileDocumentLanding && 'max-lg:hidden',
        )}
        data-mobile-document-reader
      >
        <div className='relative min-h-0 flex-1'>
          <DocReaderProvider
            autoScroll={autoScroll}
            documentSummary={documentSummary}
            fetchChapters={fetchChapters}
            logSettings={{
              parentLogger: rootLogger,
              logOptions: {
                level: 'Warn',
              },
            }}
          >
            <DocSearchBridge
              readerDocHandleRef={readerDocHandleRef}
              viewMode={content.viewMode}
            />
            <div className='relative h-full'>
              {/* SSR + first client render: always mount so chapter text is in
                  the served HTML. Post-hydration: keep mounted only when the
                  user is actually reading text — drop it in PDF mode so AT
                  users don't hear it twice. */}
              {(!hasHydrated ||
                (content.viewMode !== 'pdf' && !useDocPackTextReader)) && (
                <div
                  aria-hidden={content.viewMode === 'pdf' ? 'true' : undefined}
                  className={content.viewMode === 'pdf' ? 'sr-only' : 'h-full'}
                  data-testid='docs-text-reader'
                  data-text-reader-ssr
                >
                  <ReaderViewTanStack
                    onIsActiveVisibleChange={(isActiveVisible) => {
                      setIsCurrentTextVisible(isActiveVisible);
                    }}
                    ref={
                      content.viewMode === 'pdf'
                        ? undefined
                        : readerDocHandleRef
                    }
                    renders={renderOverrides}
                    scrollContainerClassName={`flex min-h-5 w-full min-w-0 flex-col p-2 ${docFontClass} ${docFontWeightClass} ${docTextSizeClass} *:snap-start *:scroll-m-16 md:p-4 lg:px-8`}
                    scrollContainerParentClassName={`mx-auto flex w-full min-w-0 ${docContentWidthClass} flex-col items-center`}
                    scrollHeader={
                      content.viewMode === 'pdf' ? undefined : scrollHeader
                    }
                  />
                </div>
              )}
              {hasHydrated && useDocPackTextReader && (
                <Suspense fallback={loadingFallback}>
                  <LazyDocPackReaderView
                    onIsActiveVisibleChange={(isActiveVisible) => {
                      setIsCurrentTextVisible(isActiveVisible);
                    }}
                    ref={readerDocHandleRef}
                    scrollContainerClassName={`flex min-h-5 w-full min-w-0 flex-col p-2 ${docFontClass} ${docFontWeightClass} ${docTextSizeClass} *:snap-start *:scroll-m-16 md:p-4 lg:px-8`}
                    scrollContainerParentClassName={`mx-auto flex w-full min-w-0 ${docContentWidthClass} flex-col items-center`}
                    scrollHeader={scrollHeader}
                  />
                </Suspense>
              )}

              {content.viewMode === 'pdf' &&
                (isLgViewport || !showMobileDocumentLanding) && (
                  <ClientOnly fallback={pdfLoadingFallback}>
                    <Suspense fallback={pdfLoadingFallback}>
                      <LazyDocumentView
                        documentWrapperClassName='flex size-full flex-col items-center'
                        onIsActiveVisibleChange={(isActiveVisible) => {
                          setIsCurrentTextVisible(isActiveVisible);
                        }}
                        pageWrapperClassName='relative mx-auto w-fit border max-w-[calc(100vw-0.5rem)] sm:max-w-[calc(100vw-1rem)] md:max-w-[calc(100vw-2rem)]'
                        pdfScale={pdfScale}
                        pdfUrl={content.documentUrl}
                        ref={readerDocHandleRef}
                        scrollContainerClassName='mx-auto w-full scroll-smooth px-1 sm:px-2 md:px-4 [&>*]:mx-auto'
                        scrollHeader={scrollHeader}
                      />
                    </Suspense>
                  </ClientOnly>
                )}

              <JumpToCurrentButton
                onClick={() => {
                  readerDocHandleRef.current?.scrollToCurrent();
                }}
                showJumpBtn={showJumpBtn}
              />
            </div>
          </DocReaderProvider>
        </div>
      </div>
      <DocsRightSidebar
        metadata={metadata}
        dbDoc={dbDoc}
        relatedDocs={relatedDocs}
        useOverviewPopup={showOverviewPopup}
      />
    </div>
  );
}

function DocsMobileDocumentLanding({
  authors,
  category,
  coverImageUrl,
  documentUrl,
  onViewPdf,
  pageCount,
  readingTimeMinutes,
  source,
  title,
}: {
  authors?: string | null;
  category?: string | null;
  coverImageUrl?: string | null;
  documentUrl: string;
  onViewPdf: () => void;
  pageCount?: number | null;
  readingTimeMinutes: number | null;
  source?: string | null;
  title: string;
}) {
  const { capture } = useAnalytics();

  return (
    <div
      className='min-h-0 flex-1 overflow-y-auto bg-[#F7F9FC] px-4 pt-3 pb-16 lg:hidden dark:bg-[#070b14]'
      data-mobile-document-landing
    >
      <div className='mx-auto mb-4 flex h-10 w-full max-w-xl items-center pr-32'>
        <Link
          aria-label={m.public_docs_back_to_document_repository()}
          className='inline-flex h-10 items-center gap-1.5 rounded-full px-2.5 text-sm font-medium text-[#0E5A8E] transition-colors hover:bg-white/80 dark:text-slate-200 dark:hover:bg-slate-800'
          to={localizeHref('/doc')}
        >
          <ArrowLeft className='size-5' />
          {m.common_back()}
        </Link>
      </div>
      <div className='mx-auto flex w-full max-w-xl flex-col gap-6 rounded-3xl border border-[#E2E8EE] bg-white p-5 shadow-[0_16px_50px_rgba(3,49,75,0.08)] sm:p-8 dark:border-slate-700 dark:bg-[#0a0e1a]'>
        <div className='flex items-start gap-4'>
          <div className='relative flex aspect-[3/4] w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#E2E8EE] bg-[#F5F8FB] shadow-[8px_8px_0_0_#E8EEF5] dark:border-slate-700 dark:bg-slate-900 dark:shadow-[8px_8px_0_0_#172033]'>
            {coverImageUrl ? (
              <img
                alt=''
                className='size-full object-cover'
                loading='eager'
                src={coverImageUrl}
              />
            ) : (
              <Page className='size-9 text-[#5A7387] dark:text-slate-400' />
            )}
            <span className='absolute top-1.5 left-1.5 rounded bg-[#1a1a2e] px-1.5 py-0.5 text-[9px] font-semibold text-white'>
              PDF
            </span>
          </div>
          <div className='min-w-0 flex-1 pt-1'>
            <h1 className='text-2xl leading-tight font-semibold text-balance break-words text-[#0B1F33] sm:text-3xl dark:text-slate-100'>
              {title}
            </h1>
            {authors && (
              <p className='mt-3 text-sm leading-6 text-[#425B70] dark:text-slate-300'>
                {authors}
              </p>
            )}
            {readingTimeMinutes && (
              <p className='mt-2 text-sm font-medium text-[#5A7387] dark:text-slate-400'>
                {m.public_doc_estimated_reading_time({
                  minutes: readingTimeMinutes.toString(),
                })}
              </p>
            )}
          </div>
        </div>

        {(source || category || pageCount) && (
          <dl className='grid gap-3 rounded-2xl bg-[#F5F8FB] p-4 text-sm dark:bg-slate-900/70'>
            {source && (
              <div className='grid grid-cols-[5rem_1fr] gap-3'>
                <dt className='font-medium text-[#71869A] dark:text-slate-500'>
                  {m.public_docs_source()}
                </dt>
                <dd className='min-w-0 break-words text-[#24445C] dark:text-slate-300'>
                  {source}
                </dd>
              </div>
            )}
            {category && (
              <div className='grid grid-cols-[5rem_1fr] gap-3'>
                <dt className='font-medium text-[#71869A] dark:text-slate-500'>
                  {m.public_docs_category()}
                </dt>
                <dd className='min-w-0 break-words text-[#24445C] dark:text-slate-300'>
                  {category}
                </dd>
              </div>
            )}
            {pageCount && (
              <div className='grid grid-cols-[5rem_1fr] gap-3'>
                <dt className='font-medium text-[#71869A] dark:text-slate-500'>
                  {m.common_page()}
                </dt>
                <dd className='text-[#24445C] dark:text-slate-300'>
                  {m.common_page_count({ count: pageCount })}
                </dd>
              </div>
            )}
          </dl>
        )}

        <div className='grid gap-3'>
          <Button
            className='h-14 rounded-2xl bg-[#1455C0] text-base font-semibold text-white hover:bg-[#1049A7]'
            onClick={() => {
              capture('public_doc_mobile_document_action', {
                action: 'view_pdf',
              });
              onViewPdf();
            }}
          >
            <Page className='size-5' />
            {m.public_docs_view_full_pdf()}
          </Button>
          <Button
            asChild
            className='h-14 rounded-2xl border-[#1455C0] text-base font-semibold text-[#1455C0] hover:bg-[#1455C0]/5 hover:text-[#1049A7] dark:text-blue-300'
            variant='outline'
          >
            <a
              download
              href={documentUrl}
              onClick={() => {
                capture('public_doc_mobile_document_action', {
                  action: 'download_pdf',
                });
              }}
              rel='noreferrer'
              target='_blank'
            >
              <Download className='size-5' />
              {m.common_download()} PDF
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}

function DocsReaderHeader({
  readingTimeMinutes,
  showGiantTitle,
  title,
}: {
  readingTimeMinutes: number | null;
  showGiantTitle: boolean;
  title: string;
}) {
  return (
    <div
      className={cn(
        'w-full px-4 text-center sm:px-8 lg:px-12',
        showGiantTitle
          ? 'py-5 sm:py-8 lg:py-10'
          : 'border-b border-[#E2E8EE] py-3 dark:border-slate-700',
      )}
      data-doc-reader-header
    >
      {showGiantTitle && (
        <h1 className='mx-auto max-w-5xl text-[clamp(2rem,5vw,4.5rem)] leading-[1.05] font-bold tracking-tight text-balance break-words text-slate-950 dark:text-white'>
          {title}
        </h1>
      )}
      {readingTimeMinutes && (
        <p
          className={cn(
            'text-sm font-medium text-slate-500 dark:text-slate-400',
            showGiantTitle && 'mt-3',
          )}
        >
          {m.public_doc_estimated_reading_time({
            minutes: readingTimeMinutes.toString(),
          })}
        </p>
      )}
    </div>
  );
}

function DocSearchBridge({
  readerDocHandleRef,
  viewMode,
}: {
  readerDocHandleRef: React.RefObject<DocReaderViewHandle | null>;
  viewMode: 'pdf' | 'normal';
}) {
  useDocSearch();

  const currentMatchIndex = useDocSearchStore((s) => s.currentMatchIndex);
  const matches = useDocSearchStore((s) => s.matches);

  useEffect(() => {
    const match = matches[currentMatchIndex];
    if (!match) return;

    if (viewMode === 'pdf' && match.type !== 'doc-pack') {
      const firstBbox = match.word.bboxes[0];
      if (firstBbox) {
        readerDocHandleRef.current?.scrollToIndex(firstBbox.page - 1);
      }
    } else {
      readerDocHandleRef.current?.scrollToIndex(match.scrollableIdx);
    }
  }, [currentMatchIndex, matches, readerDocHandleRef, viewMode]);

  return null;
}

function DocsChapterHeaderRender({
  chapterSummary,
}: {
  chapterSummary: { title: string };
}) {
  return (
    <div className='snap-start scroll-m-16 px-3 pt-4'>
      <h3 className='text-left text-2xl leading-[1.2] font-bold [overflow-wrap:anywhere] sm:text-3xl md:text-[2.5rem]'>
        {chapterSummary.title}
      </h3>
    </div>
  );
}

function DocsParagraphRender({
  paragraph,
  isActive: _isActive,
  firstActive: _firstActive,
  children,
  paragraphIdx: _paragraphIdx,
  ref,
}: {
  ref: React.Ref<HTMLDivElement>;
  type: 'paragraph';
  chapterIdx: number;
  chapterSummary: { title: string; id: string };
  paragraph: Paragraph;
  scrollableIdx: number;
  chapterId: string;
  paragraphIdx: number;
  isActive: boolean;
  lastActive: boolean;
  firstActive: boolean;
  children: React.ReactNode;
}) {
  const docLineHeightClass = getDocLineHeightClassName(
    useAudioPrefsStore((s) => s.docLineHeight),
  );
  const docParagraphSpacingClass = getDocParagraphSpacingClassName(
    useAudioPrefsStore((s) => s.docParagraphSpacing),
  );

  // Clicking the paragraph (between words) seeks to its first word — resolved
  // by the view-level delegated handler via `data-seek-*`.
  const firstWord = paragraph.sentences[0]?.words[0];

  return (
    <div className='relative snap-start scroll-m-16' ref={ref}>
      <div
        className={cn('px-3', docParagraphSpacingClass)}
        data-seek-chunk-id={firstWord?.chunkId}
        data-seek-page={firstWord?.bboxes[0]?.page}
        data-seek-word-idx={firstWord?.wordIdxInChunk}
        role='button'
        tabIndex={0}
      >
        <p
          className={cn(
            'cursor-pointer [overflow-wrap:anywhere] break-words',
            docLineHeightClass,
          )}
        >
          {children}
        </p>
      </div>
    </div>
  );
}

function DocsWordRender({ word, spanProps }: WordRenderProps) {
  const searchMatch = useIsSearchMatch(word);
  const docActiveEmphasis = useAudioPrefsStore((s) => s.docActiveEmphasis);
  const registryRef = useLegacyWordRegistryRef(
    word.chunkId,
    word.wordIdxInChunk,
    {
      chapterId: spanProps['data-chapter-id'],
      paragraphIdx: spanProps['data-paragraph-idx'],
      sentenceIdx: spanProps['data-sentence-idx'],
    },
  );

  return (
    <span
      ref={registryRef}
      className={cn(
        'cursor-pointer',
        getDocsActiveWordClassName(docActiveEmphasis),
        searchMatch === 'current' && 'bg-yellow-300 dark:bg-yellow-500/60',
        searchMatch === 'other' && 'bg-yellow-200/60 dark:bg-yellow-700/40',
      )}
      role='button'
      tabIndex={-1}
      {...spanProps}
    >
      {word.text}{' '}
    </span>
  );
}

function DocsLeftSidebar({
  items,
  onItemClick,
}: {
  items: DocsLeftSidebarItem[];
  onItemClick?: (item: DocsLeftSidebarItem) => void;
}) {
  // Widened by pointer hover OR keyboard focus: hover alone left keyboard users
  // with no way to read the contents list.
  const [hovered, setHovered] = useState(false);

  if (items.length === 0) return null;

  const expanded = hovered;

  return (
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- pointer and focus handlers only widen this landmark; every control inside stays reachable at either width
    <aside
      aria-labelledby='doc-contents-heading'
      className='hidden min-h-0 shrink-0 flex-col overflow-hidden border-r border-[#E2E8EE] bg-white transition-[width] duration-200 ease-out lg:flex dark:border-slate-700 dark:bg-[#0a0e1a]'
      style={{ width: expanded ? 260 : 56 }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setHovered(false);
      }}
      onFocus={() => setHovered(true)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={cn(
          'min-h-0 flex-1 overflow-x-hidden py-5',
          expanded ? 'overflow-y-auto' : 'overflow-y-hidden',
        )}
      >
        <h4
          className={cn(
            'mb-3 pr-4 pl-[56px] text-[11px] font-semibold tracking-[0.08em] whitespace-nowrap text-[#94A4B5] uppercase transition-opacity duration-150 dark:text-slate-500',
            expanded ? 'opacity-100' : 'opacity-0',
          )}
          id='doc-contents-heading'
        >
          Contents
        </h4>
        <nav className='flex flex-col'>
          {items.map((item) => (
            <button
              key={`${item.type}:${item.id}`}
              type='button'
              className='flex w-full items-center py-1.5 pr-4 text-left text-[13px] whitespace-nowrap text-[#5A7387] transition-colors hover:text-[#03314B] dark:text-slate-400 dark:hover:text-slate-200'
              onClick={() => onItemClick?.(item)}
              style={
                item.type === 'doc-pack'
                  ? { paddingLeft: `${item.depth * 10}px` }
                  : undefined
              }
              title={item.title}
            >
              <span
                aria-hidden='true'
                className='grid h-full w-[56px] shrink-0 place-items-center'
              >
                <span
                  className={cn(
                    'size-1 rounded-full',
                    expanded
                      ? 'bg-[#E2E8EE] dark:bg-slate-700'
                      : 'bg-[#94A4B5] dark:bg-slate-600',
                  )}
                />
              </span>
              <span
                className={cn(
                  'min-w-0 flex-1 overflow-hidden text-ellipsis transition-opacity duration-150',
                  expanded ? 'opacity-100' : 'opacity-0',
                )}
              >
                {item.title}
              </span>
            </button>
          ))}
        </nav>
      </div>
      <div
        className={cn(
          'shrink-0 transition-opacity duration-150',
          expanded ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <DocsSearchPanel />
      </div>
    </aside>
  );
}

function DocsSearchPanel() {
  const query = useDocSearchStore((s) => s.query);
  const setQuery = useDocSearchStore((s) => s.setQuery);
  const matches = useDocSearchStore((s) => s.matches);
  const currentMatchIndex = useDocSearchStore((s) => s.currentMatchIndex);
  const nextMatch = useDocSearchStore((s) => s.nextMatch);
  const prevMatch = useDocSearchStore((s) => s.prevMatch);
  const clearSearch = useDocSearchStore((s) => s.clearSearch);
  const loadedChapterCount = useDocSearchStore((s) => s.loadedChapterCount);
  const totalChapterCount = useDocSearchStore((s) => s.totalChapterCount);

  const hasQuery = query.trim().length > 0;
  const isPartialSearch =
    hasQuery && totalChapterCount > 0 && loadedChapterCount < totalChapterCount;

  return (
    <div className='shrink-0 border-t border-[#E2E8EE] p-3 dark:border-slate-700'>
      <div className='relative'>
        <Search className='absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-[#5A7387] dark:text-muted-foreground' />
        <input
          className='h-8 w-full rounded-lg border border-[#E2E8EE] bg-[#F5F8FB] pr-2.5 pl-8 text-sm text-[#03314B] placeholder:text-[#94A4B5] focus:border-[#1E7FBF] focus:ring-1 focus:ring-[#1E7FBF]/30 focus:outline-none dark:border-slate-700 dark:bg-transparent dark:text-foreground dark:placeholder:text-muted-foreground'
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              clearSearch();
              e.currentTarget.blur();
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              nextMatch();
            }
            if (e.key === 'Enter' && e.shiftKey) {
              e.preventDefault();
              prevMatch();
            }
          }}
          aria-label={m.read_along_search_in_document()}
          placeholder={m.common_search_placeholder()}
          type='text'
          value={query}
        />
      </div>
      {hasQuery && (
        <div className='mt-2 flex items-center justify-between'>
          <span className='text-xs text-muted-foreground'>
            {matches.length > 0
              ? m.read_along_search_result_position({
                  current: currentMatchIndex + 1,
                  total: matches.length,
                })
              : m.read_along_search_no_results()}
            {isPartialSearch && (
              <span className='text-muted-foreground/60'>
                {' '}
                {m.read_along_search_chapter_progress({
                  loaded: loadedChapterCount,
                  total: totalChapterCount,
                })}
              </span>
            )}
          </span>
          <div className='flex items-center gap-0.5'>
            <Button
              aria-label={m.read_along_previous_match()}
              className='size-6 p-0'
              disabled={matches.length === 0}
              onClick={prevMatch}
              variant='ghost'
            >
              <NavArrowUp className='size-3.5' />
            </Button>
            <Button
              aria-label={m.read_along_next_match()}
              className='size-6 p-0'
              disabled={matches.length === 0}
              onClick={nextMatch}
              variant='ghost'
            >
              <NavArrowDown className='size-3.5' />
            </Button>
            <Button
              aria-label={m.read_along_clear_search()}
              className='size-6 p-0'
              onClick={clearSearch}
              variant='ghost'
            >
              <Xmark className='size-3.5' />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const RIGHT_SIDEBAR_WIDTH = 400;

function DocsRightSidebar({
  metadata,
  dbDoc,
  relatedDocs = [],
  useOverviewPopup,
}: {
  metadata?: GoogleDocPublicPdfData;
  dbDoc?: DbDocData;
  relatedDocs?: ReadonlyArray<RelatedPublicDoc>;
  useOverviewPopup: boolean;
}) {
  const [open, setOpen] = useState(true);
  const { capture } = useAnalytics();
  const summary = metadata?.abstract || dbDoc?.summary;
  const authors = metadata?.authors || dbDoc?.publishingAuthor;
  const source = metadata?.source;
  const category =
    dbDoc?.category ??
    (metadata?.category ? resolvePublicDocCategory(metadata.category) : null);
  const keyPoints = dbDoc?.keyPoints;

  const keyPointsList = documentKeyPoints(keyPoints);

  const hasContent =
    summary ||
    authors ||
    source ||
    category ||
    keyPointsList?.length ||
    relatedDocs.length;
  if (!hasContent) return null;

  const sectionProps = {
    authors,
    category,
    keyPoints: keyPointsList,
    relatedDocs,
    source,
    summary,
  };

  if (useOverviewPopup) {
    return (
      <Dialog
        onOpenChange={(isOpen) => {
          if (!isOpen) return;
          capture('public_doc_overview_popup_opened', {
            viewport: 'desktop',
          });
        }}
      >
        <DialogTrigger asChild>
          <button
            type='button'
            className='absolute top-3 right-3 z-20 hidden h-9 items-center gap-2 rounded-full border border-[#D9E2EF] bg-white/95 px-3 text-sm font-medium text-[#0E5A8E] shadow-[0_2px_10px_rgba(3,49,75,0.10)] backdrop-blur-sm transition-colors hover:bg-[#F5F8FB] focus-visible:ring-2 focus-visible:ring-[#1E7FBF] focus-visible:outline-none lg:inline-flex dark:border-slate-700 dark:bg-[#0a0e1a]/95 dark:text-slate-200 dark:hover:bg-slate-800'
            data-overview-popup-trigger
          >
            <LightBulb className='size-4' />
            {m.public_docs_summary()}
          </button>
        </DialogTrigger>
        <DialogContent
          className='max-h-[min(88dvh,760px)] w-[calc(100vw-1.5rem)] max-w-2xl gap-0 overflow-y-auto rounded-2xl border-[#E2E8EE] bg-white p-0 dark:border-slate-700 dark:bg-[#0a0e1a]'
          includeCloseButton
        >
          <DialogHeader className='border-b border-[#E2E8EE] px-5 py-5 pr-12 text-left sm:px-6 dark:border-slate-700'>
            <DialogTitle className='text-xl text-[#03314B] dark:text-slate-100'>
              {m.public_docs_summary()}
            </DialogTitle>
            <DialogDescription>
              {m.public_docs_overview_popup_description()}
            </DialogDescription>
          </DialogHeader>
          <div className='flex flex-col gap-8 px-5 py-6 sm:px-6'>
            <DocsSidebarSections {...sectionProps} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const closeButton = (
    <button
      type='button'
      aria-label='Hide details'
      onClick={() => setOpen(false)}
      className='-mr-1 grid size-7 shrink-0 place-items-center rounded-md text-[#5A7387] transition-colors hover:bg-[#F5F8FB] hover:text-[#03314B] dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200'
    >
      <Xmark className='size-4' />
    </button>
  );

  return (
    <>
      <aside
        aria-label='Document details'
        className='hidden min-h-0 shrink-0 flex-col overflow-hidden border-l border-[#E2E8EE] bg-white transition-[width] duration-200 ease-out lg:flex dark:border-slate-700 dark:bg-[#0a0e1a]'
        style={{ width: open ? RIGHT_SIDEBAR_WIDTH : 0 }}
      >
        <div
          className='flex h-full flex-col'
          style={{ width: RIGHT_SIDEBAR_WIDTH }}
        >
          <div className='flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto p-6'>
            <DocsSidebarSections {...sectionProps} action={closeButton} />
          </div>
        </div>
      </aside>
      {!open && (
        <button
          type='button'
          aria-label='Show details'
          onClick={() => setOpen(true)}
          className='absolute top-1/2 right-3 z-10 hidden size-9 -translate-y-1/2 items-center justify-center rounded-full border border-[#E2E8EE] bg-white text-[#5A7387] shadow-[0_2px_8px_rgba(3,49,75,0.08)] transition hover:text-[#03314B] lg:flex dark:border-slate-700 dark:bg-[#0a0e1a] dark:text-slate-400 dark:hover:text-slate-200'
        >
          <NavArrowLeft className='size-4' />
        </button>
      )}
    </>
  );
}

function DocsSidebarSections({
  action,
  authors,
  category,
  keyPoints,
  relatedDocs,
  source,
  summary,
}: {
  action?: React.ReactNode;
  authors?: string | null;
  category?: string | null;
  keyPoints?: string[];
  relatedDocs: ReadonlyArray<RelatedPublicDoc>;
  source?: string | null;
  summary?: string | null;
}) {
  let actionAssigned = false;
  const takeAction = () => {
    if (actionAssigned) return undefined;
    actionAssigned = true;
    return action;
  };

  return (
    <>
      {summary && (
        <DocumentSummarySection summary={summary} action={takeAction()} />
      )}
      {keyPoints && keyPoints.length > 0 && (
        <DocumentKeyPointsSection points={keyPoints} action={takeAction()} />
      )}
      {(authors || source || category) && (
        <DocumentDetailsSection
          authors={authors}
          source={source}
          category={category}
          action={takeAction()}
        />
      )}
      {relatedDocs.length > 0 && (
        <DocsSidebarRelated
          relatedDocs={relatedDocs.slice(0, 5)}
          action={takeAction()}
        />
      )}
    </>
  );
}

function DocsSidebarRelated({
  relatedDocs,
  action,
}: {
  relatedDocs: ReadonlyArray<RelatedPublicDoc>;
  action?: React.ReactNode;
}) {
  return (
    <section>
      <SidebarEyebrow action={action}>You might also like</SidebarEyebrow>
      <ul className='flex flex-col'>
        {relatedDocs.map((doc) => (
          <li
            key={doc.publicId}
            className='border-b border-[#E2E8EE] py-3 first:pt-0 last:border-b-0 dark:border-slate-800'
          >
            <a href={`/doc/${doc.slug}`} className='group flex gap-3'>
              <div className='relative h-[72px] w-[52px] shrink-0 overflow-hidden rounded-sm border border-[#E2E8EE] bg-[#F5F8FB] dark:border-slate-700 dark:bg-slate-800'>
                {doc.coverImageUrl ? (
                  <img
                    src={doc.coverImageUrl}
                    alt=''
                    className='size-full object-cover'
                  />
                ) : (
                  <div className='flex size-full items-center justify-center'>
                    <div className='text-[8px] font-medium text-[#94A4B5] dark:text-slate-500'>
                      PDF
                    </div>
                  </div>
                )}
                <div className='absolute top-1 left-1 rounded-[3px] bg-[#1a1a2e] px-1 py-0.5 text-[8px] font-semibold text-white'>
                  PDF
                </div>
              </div>
              <div className='flex min-w-0 flex-1 flex-col justify-center'>
                <h4 className='line-clamp-2 text-[13px] leading-snug font-semibold text-[#03314B] group-hover:text-[#1E7FBF] dark:text-foreground dark:group-hover:text-[#5daae3]'>
                  {doc.title || 'Untitled document'}
                </h4>
                {doc.description && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className='mt-1 line-clamp-1 text-[11px] leading-relaxed text-[#5A7387] dark:text-muted-foreground'>
                        {doc.description}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent
                      side='bottom'
                      align='start'
                      className='max-w-[280px] text-xs'
                    >
                      {doc.description}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export const docsRenderOverrides: ReaderRenderOverrides = {
  ChapterHeaderRender: DocsChapterHeaderRender,
  ParagraphRender: DocsParagraphRender,
  WordRender: DocsWordRender,
};
